"use strict";
var __knownSymbol = (name, symbol) => (symbol = Symbol[name]) ? symbol : Symbol.for("Symbol." + name);
var __typeError = (msg) => {
  throw TypeError(msg);
};
var __using = (stack, value, async) => {
  if (value != null) {
    if (typeof value !== "object" && typeof value !== "function") __typeError("Object expected");
    var dispose, inner;
    if (async) dispose = value[__knownSymbol("asyncDispose")];
    if (dispose === void 0) {
      dispose = value[__knownSymbol("dispose")];
      if (async) inner = dispose;
    }
    if (typeof dispose !== "function") __typeError("Object not disposable");
    if (inner) dispose = function() {
      try {
        inner.call(this);
      } catch (e) {
        return Promise.reject(e);
      }
    };
    stack.push([async, dispose, value]);
  } else if (async) {
    stack.push([async]);
  }
  return value;
};
var __callDispose = (stack, error, hasError) => {
  var E = typeof SuppressedError === "function" ? SuppressedError : function(e, s, m, _) {
    return _ = Error(m), _.name = "SuppressedError", _.error = e, _.suppressed = s, _;
  };
  var fail = (e) => error = hasError ? new E(e, error, "An error was suppressed during disposal") : (hasError = true, e);
  var next = (it) => {
    while (it = stack.pop()) {
      try {
        var result = it[1] && it[1].call(it[2]);
        if (it[0]) return Promise.resolve(result).then(next, (e) => (fail(e), next()));
      } catch (e) {
        fail(e);
      }
    }
    if (hasError) throw error;
  };
  return next();
};
/**
 * @license
 * Copyright 2024 Google Inc.
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *      http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { read, stat } from "#src/kvstore/http/read.js";
import { encodePathForUrl } from "#src/kvstore/url.js";
import { fetchOk } from "#src/util/http_request.js";
import {
  parseArray,
  verifyObject,
  verifyObjectProperty,
  verifyOptionalObjectProperty,
  verifyString,
  verifyStringArray
} from "#src/util/json.js";
import { ProgressSpan } from "#src/util/progress_listener.js";
import { getRandomHexString } from "#src/util/random.js";
export class GcsKvStore {
  constructor(bucket, baseUrlForDisplay = `gs://${bucket}/`, fetchOkImpl = fetchOk) {
    this.bucket = bucket;
    this.baseUrlForDisplay = baseUrlForDisplay;
    this.fetchOkImpl = fetchOkImpl;
  }
  getObjectUrl(key) {
    return `https://storage.googleapis.com/storage/v1/b/${this.bucket}/o/${encodeURIComponent(key)}?alt=media&neuroglancer=${getRandomHexString()}`;
  }
  stat(key, options) {
    return stat(this, key, this.getObjectUrl(key), options, this.fetchOkImpl);
  }
  read(key, options) {
    return read(this, key, this.getObjectUrl(key), options, this.fetchOkImpl);
  }
  async list(prefix, options) {
    var _stack = [];
    try {
      const { progressListener } = options;
      const _span = __using(_stack, progressListener === void 0 ? void 0 : new ProgressSpan(progressListener, {
        message: `Listing prefix ${this.getUrl(prefix)}`
      }));
      const delimiter = "/";
      const response = await this.fetchOkImpl(
        `https://storage.googleapis.com/storage/v1/b/${this.bucket}/o?delimiter=${encodeURIComponent(delimiter)}&prefix=${encodeURIComponent(
          prefix
        )}&neuroglancerOrigin=${encodeURIComponent(location.origin)}`,
        {
          signal: options.signal,
          progressListener: options.progressListener
        }
      );
      const responseJson = await response.json();
      verifyObject(responseJson);
      const directories = verifyOptionalObjectProperty(
        responseJson,
        "prefixes",
        verifyStringArray,
        []
      ).map((prefix2) => prefix2.substring(0, prefix2.length - 1));
      const entries = verifyOptionalObjectProperty(
        responseJson,
        "items",
        (items) => parseArray(items, (item) => {
          verifyObject(item);
          return verifyObjectProperty(item, "name", verifyString);
        }),
        []
      ).filter((name) => !name.endsWith("_$folder$")).map((name) => ({ key: name }));
      return {
        directories,
        entries
      };
    } catch (_) {
      var _error = _, _hasError = true;
    } finally {
      __callDispose(_stack, _error, _hasError);
    }
  }
  getUrl(path) {
    return this.baseUrlForDisplay + encodePathForUrl(path);
  }
  get supportsOffsetReads() {
    return true;
  }
  get supportsSuffixReads() {
    return true;
  }
}
